/**
 * True only while an Act As (impersonation) session is active.
 *
 * The backend populates `user.PUBLICID` with the currently-viewed account id
 * (a number) and `user.ActingAsPUBLICID` with the real actor's id — but only
 * during an Act As session (e.g. the InternalBAY → Act As flow). On a direct
 * login there is no actor, so the backend sends `ActingAsPUBLICID` as an empty
 * string (see views/layouts/main.php:
 * `'ActingAsPUBLICID' => $acting_as_actor_id ?? ""`).
 *
 * A raw `ActingAsPUBLICID !== PUBLICID` therefore reports "acting as" on a
 * direct login too, because `"" !== 12345` is true (empty string vs number).
 * This guards against all three pitfalls:
 *   - requires a non-empty `ActingAsPUBLICID` (no direct-login false positive)
 *   - requires a non-empty `PUBLICID` (a real Act As session always has a
 *     viewed account; without one there is nothing to be "acting as")
 *   - coerces both ids to strings before comparing (no number-vs-string mismatch)
 *
 */
interface ActingAsUser {
	ActingAsPUBLICID?: string | number;
	PUBLICID?: string | number;
}

const isActingAsAnotherUser = (user?: ActingAsUser | Record<string, any>): boolean => {
	const actingAsPublicId = String(user?.ActingAsPUBLICID ?? '');
	const userPublicId = String(user?.PUBLICID ?? '');

	return (
		actingAsPublicId.length > 0 &&
		userPublicId.length > 0 &&
		actingAsPublicId !== userPublicId
	);
};

export default isActingAsAnotherUser;
